/**
 * Copyright (C) 2019 Viasat, Inc.
 * All rights reserved.
 * The information in this software is subject to change without notice and should not be construed as a commitment by Viasat, Inc.
 *
 * Viasat Proprietary
 * The Proprietary Information provided herein is proprietary to Viasat and
 * must be protected from further distribution and use. Disclosure to others,
 * use or copying without express written authorization of Viasat, is strictly prohibited.
 */

import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { Box, Button } from '@mui/material';
import { isNil } from 'lodash';
import React, { useEffect, useState } from 'react';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import appTheme from '../../../../components/common/theme/appTheme.json';
import { InstallationType, TerminalTypeLabel } from '../../../../shared/entities/terminal';
import { selectTerminalDetails } from '../../../../store';
import { setTheAllProtocolModalData } from '../../../../store/terminalHistory/actions';
import { RateKeys } from '../../../../store/terminalHistory/reducer';
import { getTerminalDetailsBasedOnTerminalType } from '../../../../utils/TerminalUtilities';
import { useWindowDimensions } from '../../PlatformListContainer';
import { useLocation } from 'react-router-dom';

interface ViewAllProtocolsProps {
  type: RateKeys;
  terminalType?: TerminalTypeLabel;
}
function ViewAllProtocols({ type, terminalType }: ViewAllProtocolsProps) {
  const terminalDetails = useSelector(selectTerminalDetails, shallowEqual);
  const { width } = useWindowDimensions();
  const dispatch = useDispatch();

  // Check if the page is in export mode and determine the 'View all' button spacing
  const currentRoute = useLocation().pathname;
  const isInExportMode = currentRoute.slice(-7).match('/export');
  var distanceMarginLeft;
  if (isInExportMode) {
    distanceMarginLeft = 900;
  } else {
    distanceMarginLeft = width - 612;
  }

  /**
   * After the chart is loaded, there is some delay plotting the chart.
   *  This was added as a workaround to hide the 'View all' the chart is completely loaded and plotted.
   */
  const [isPlotting, setIsPlotting] = useState<boolean>(true);

  /** hide 'View all' for one event cycle or 0ms */
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      setIsPlotting(false);
    }, 0);

    return () => {
      //cancel timeout
      clearTimeout(timeoutId);
    };
  }, []);

  const setProtocolsData = () => {
    if (!isNil(terminalDetails)) {
      let termDetails = terminalDetails;
      let isOtherTermHistory = false;

      // get either terminal details or other terminal details
      if (terminalDetails && terminalDetails.installationType === InstallationType.KU_PLUS_KA) {
        termDetails = getTerminalDetailsBasedOnTerminalType(termDetails, terminalType);
        isOtherTermHistory = terminalDetails.datapathUuid !== termDetails.datapathUuid;
      }

      dispatch(setTheAllProtocolModalData(termDetails.trafficSourceTime, type, isOtherTermHistory));
    }
  };

  const onViewAll = () => {
    dispatch(setTheAllProtocolModalData(undefined, undefined, undefined));
    setProtocolsData();
  };

  return !isPlotting ? (
    <Box
      sx={{
        display: width < 600 ? 'none' : 'flex',
        position: 'absolute',
        marginLeft: `${distanceMarginLeft}px`,
        bottom: 30,
      }}
    >
      <Button
        variant="text"
        onClick={onViewAll}
        disableRipple
        sx={{
          lineHeight: '16px',
          fontSize: '12px',
          fontWeight: 400,
          fontFamily: appTheme.appFontFamily,
          color: appTheme.mptLightBlue,
          ':hover': {
            color: appTheme.mptLightBlue,
            svg: {
              color: appTheme.mptLightBlue,
            },
          },
        }}
        endIcon={
          <OpenInNewIcon
            sx={{
              width: '12px',
              height: '12px',
              color: appTheme.mptLightBlue,
            }}
          />
        }
      >
        View all
      </Button>
    </Box>
  ) : null;
}

export default ViewAllProtocols;
